// A fraction as a ring: a track and an arc whose length is the share done. MasteryRing puts the
// percentage in the middle (skill only, never a count of anything drunk).
import "./config";
import { View } from "tamagui";
import { useT } from "../lib/i18n";
import { paint } from "./Graphic";
import { VisuallyHidden } from "./layout";
import { Body } from "./text";

export interface ProgressRingProps {
  done: number;
  total: number;
  size?: number;
  /** The ring's colour; a complete ring always takes the ready colour. */
  tone?: "accent" | "ready";
}

/** pathLength="1" lets the dash offset animate the arc for any radius. */
export function ProgressRing({
  done,
  total,
  size = 44,
  tone = "accent",
}: ProgressRingProps): React.JSX.Element {
  const fraction = total <= 0 ? 0 : Math.min(1, done / total);
  return (
    <svg
      viewBox="0 0 36 36"
      width={size}
      height={size}
      role="img"
      aria-label={`${String(done)} / ${String(total)}`}
      style={{ display: "block", flexShrink: 0, transform: "rotate(-90deg)" }}
    >
      <circle
        cx="18"
        cy="18"
        r="15"
        fill="none"
        stroke={paint("surface3")}
        strokeWidth="var(--ringW)"
      />
      <circle
        cx="18"
        cy="18"
        r="15"
        fill="none"
        stroke={paint(fraction >= 1 ? "ready" : tone)}
        strokeWidth="var(--ringW)"
        strokeLinecap="round"
        pathLength={1}
        strokeDasharray={1}
        strokeDashoffset={1 - fraction}
        style={{ transition: "stroke-dashoffset var(--durSlow) ease-out" }}
      />
    </svg>
  );
}

export interface MasteryRingProps {
  /** Whole number, 0 to 100. */
  value: number;
  size?: number;
  /** A level just completed: the ring takes the ready colour. */
  ready?: boolean;
  testId?: string;
}

export function MasteryRing({
  value,
  size = 56,
  ready = false,
  testId,
}: MasteryRingProps): React.JSX.Element {
  const { t } = useT();
  return (
    <View
      position="relative"
      flexShrink={0}
      alignItems="center"
      justifyContent="center"
      width={size}
      height={size}
      // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
      {...(testId === undefined ? {} : { "data-testid": testId })}
    >
      <View aria-hidden>
        <ProgressRing done={value} total={100} size={size} tone={ready ? "ready" : "accent"} />
      </View>
      <Body
        position="absolute"
        size="small"
        weight="strong"
        numeric
        color={ready ? "ready" : "text"}
        aria-hidden
      >
        {value}%
      </Body>
      <VisuallyHidden>{t("learn.level.mastery-aria", { n: value })}</VisuallyHidden>
    </View>
  );
}
